import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import {
  isFavorited,
  useFavorites,
  useToggleFavorite
} from "@/queries/customer/favorites";
import { toast } from "@medusajs/ui";
import { Heart } from "lucide-react";
import { memo, useCallback } from "react";

interface FavoriteButtonProps {
  /** The variant this heart toggles. */
  variantId: string;
  /** The seller routing key for that variant. */
  sellerId: string;
  /** Where the heart is rendered - stamped on the analytics event. */
  source: "pdp";
  disabled?: boolean;
  className?: string;
}

/**
 * Single-tap heart icon (KAN-14) - flips membership of one `(seller_id,
 * variant_id)` line on the customer's favorites list. Unlike
 * `AddToSavedListButton`'s picker/naming dialog, this is a direct boolean
 * toggle: no dialog, optimistic (fills/empties immediately, rolls back on
 * error). Favorites is a `kind: "favorites"` saved list under the hood
 * (storefront ADR-0005 / api ADR-0002).
 */
export const FavoriteButton = memo(function FavoriteButton({
  variantId,
  sellerId,
  source,
  disabled = false,
  className
}: FavoriteButtonProps) {
  const favorites = useFavorites();
  const toggle = useToggleFavorite();

  const favorited = isFavorited(favorites.data, {
    variant_id: variantId,
    seller_id: sellerId
  });

  const handleClick = useCallback(async () => {
    try {
      const result = await toggle.mutateAsync({
        variant_id: variantId,
        seller_id: sellerId
      });
      analytics.favoriteToggled({
        variant_id: variantId,
        seller_id: sellerId,
        favorited: result.favorited,
        source
      });
    } catch (error) {
      toast.error(
        handleActionError(error, {
          boundary: "favorite toggle",
          fallback: "We couldn't update your favourites. Please try again."
        })
      );
    }
  }, [toggle, variantId, sellerId, source]);

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={className}
      onClick={handleClick}
      disabled={disabled || favorites.isLoading || toggle.isPending}
      aria-label={favorited ? "Remove from favourites" : "Add to favourites"}
      aria-pressed={favorited}
    >
      <Heart
        className={
          favorited
            ? "h-4 w-4 fill-red-500 text-red-500"
            : "h-4 w-4 text-gray-500"
        }
        aria-hidden="true"
      />
    </Button>
  );
});
